import { useEffect } from 'react'
import {
  configApi,
  dischargeReasonOptions,
  isWellnessSelector,
  patientRegistrationApi,
  StandardListItemFrag,
} from '@valerahealth/rtk-query'
import LABELS from '@valerahealth/ui-translation/locales/en'
import { useSelector } from 'react-redux'
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from '../../base'
import {
  FormProvider,
  SaveButton,
  Select,
  TextField,
  useForm,
} from '../../form'
import { CloseOutlined } from '../../icons'
import { useNotify } from '../Notifications'

type Form = {
  reason: string
  description: string
}

const dischargeFormDefaultValue: Form = {
  reason: '',
  description: '',
}

const defaultDischargeReasons: StandardListItemFrag[] = []

export type DischargeFormProps = {
  treatmentId: string
  patientId: string
  onSuccess: () => void
  onCancel: () => void
}

export type DischargePopupProps<Open extends true | false> = {
  open: Open
  patientId?: string
  treatmentId?: string
} & Omit<DischargeFormProps, 'patientId' | 'treatmentId'>

export const DischargeForm = ({
  treatmentId,
  patientId,
  onSuccess,
  onCancel,
}: DischargeFormProps) => {
  const isWellness = useSelector(isWellnessSelector)

  const notify = useNotify()
  const [dischargePatient, dischargePatientStatus] =
    patientRegistrationApi.useDischargePatientMutation()

  const methods = useForm<Form>({
    defaultValues: dischargeFormDefaultValue,
  })

  const { dischargeReasons, isLoading, error } = configApi.useGetListMapQuery(
    ['dischargeReasons'],
    {
      skip: !isWellness,
      selectFromResult: ({ data, isLoading, error }) => ({
        dischargeReasons:
          data?.dischargeReasons?.list || defaultDischargeReasons,
        isLoading,
        error,
      }),
    },
  )

  useEffect(() => {
    if (error) {
      notify({
        severity: 'warning',
        message: error.message || 'Failed to load discharge reasons list.',
      })
    }
  }, [error, notify])

  const onFormSubmit = async ({ reason, description }: Form) => {
    const value = await dischargePatient({
      treatmentId,
      input: {
        patientId,
        reason,
        description: description || undefined,
        notifyPatient: false,
      },
    })
    if ('error' in value) {
      notify({
        severity: 'error',
        message: LABELS.DischargePopup.dischargePatientError,
      })
      console.error(value.error)
    } else {
      notify({
        severity: 'success',
        message: LABELS.DischargePopup.dischargePatientSuccess,
      })
      onSuccess()
    }
  }

  return (
    <Grid
      container
      spacing={2}
      component="form"
      onSubmit={methods.handleSubmit(onFormSubmit)}
      sx={{ pt: 1 }}
    >
      <FormProvider {...methods}>
        <Grid item xs={12}>
          <Typography sx={{ mt: '-12px' }}>
            {LABELS.DischargePopup.dischargePatientWarning}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Select
            isLoading={isLoading}
            required
            fullWidth
            name="reason"
            label="Reason"
          >
            {isWellness
              ? dischargeReasons.map(({ label, value, id }) => (
                  <MenuItem key={id} value={value}>
                    {label}
                  </MenuItem>
                ))
              : dischargeReasonOptions.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
          </Select>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Additional Details"
            name="description"
            multiline
          />
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" gap={2}>
            <Button
              variant="outlined"
              color="error"
              sx={{ ml: 'auto' }}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <SaveButton
              label="DISCHARGE"
              isError={dischargePatientStatus.isError}
              isSuccess={dischargePatientStatus.isSuccess}
            />
          </Stack>
        </Grid>
      </FormProvider>
    </Grid>
  )
}

export function DischargePopup<Open extends true | false>({
  open,
  patientId,
  treatmentId,
  ...props
}: DischargePopupProps<Open>) {
  return (
    <Dialog open={!!open} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {LABELS.dischargePatient}
        <IconButton title="Close" onClick={props.onCancel}>
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {open && patientId && treatmentId && (
          <DischargeForm {...props} patientId={patientId} treatmentId={treatmentId} />
        )}
      </DialogContent>
    </Dialog>
  )
}
